import classNames from 'classnames';
import * as React from 'react';
import type { CreditType } from '../../Interfaces/CreditType';
import { usePlannerContext } from '../Planner/Context/PlannerContextProvider';
import { usePlannerTranslations } from '../Planner/Context/PlannerTranslationsContextProvider';
import { getContentLanguage, getContentLocale } from '~/Features/Configuration/Utils/languageUtils';
import type { Money } from '~/Features/Money.csharp';
import { PillTag } from '~/Features/Partials/PillTag/PillTag';
import type { SubjectBaseProps } from '~/Features/StudyPage/Common/Interfaces/SubjectBaseProps';
import { ButtonSize } from '~/Partials/Button/ButtonSize.csharp';
import { ButtonTier } from '~/Partials/Button/ButtonTier.csharp';
import { ButtonType } from '~/Partials/Button/ButtonType.csharp';
import { Link } from '~/Partials/Link/Link';
import { LinkType } from '~/Partials/Link/LinkType.csharp';
import { LinkVariant } from '~/Partials/Link/LinkVariant.csharp';
import { formatDateShort } from '~/Partials/Utils/dateUtils';
import type { StrictOmit } from '~/Partials/Utils/typeUtils';
import { assertNever } from '~/Partials/Utils/typeUtils';
import { moneyToString } from '~/Partials/Utils/utils';
import styles from './SingleSubject.module.css';
import type { SingleSubjectActionProps } from './SingleSubjectAction/SingleSubjectAction';
import { SingleSubjectAction } from './SingleSubjectAction/SingleSubjectAction';
import type { SingleSubjectDisplayType } from './SingleSubjectDisplayType';
import type { SingleSubjectSize } from './SingleSubjectSize';

type SingleSubjectExtensionsProps = StrictOmit<SubjectBaseProps, 'translations' | 'points'> &
    StrictOmit<SingleSubjectActionProps, 'onClick'>;

export interface SingleSubjectProps extends SingleSubjectExtensionsProps {
    onActionClick: () => void;
    points: {
        amount: number;
        creditType: CreditType;
    };
    canBePurchased: boolean;
    educationCategories: string[];
    linkUrl?: string;
    className?: string;
    startDate?: Date;
    displayType?: SingleSubjectDisplayType;
    size?: SingleSubjectSize;
    price?: Money;
}

export const SingleSubject: React.FC<SingleSubjectProps> = props => {
    const {
        onActionClick,
        price,
        subjectCategory,
        subjectName,
        linkUrl,
        points,
        startDate,
        canBePurchased,
        displayType = 'withTags',
        size = 'normal',
        ...restProps
    } = props;

    const { nonPurchasableSubjectsLink } = usePlannerContext();
    const { creditsUnit, description, subjectCategory: subjectCategoryTranslations } = usePlannerTranslations();

    const { specialization, elective } = subjectCategoryTranslations;
    const { subjectStarting } = description;

    const currentLanguage = getContentLanguage();
    const currentLocale = getContentLocale();

    const withTags = displayType === 'withTags';

    const getPillTag = () => {
        const pillTagBase = {
            size: 'responsiveFromXSmallToSmall',
        } as const;

        switch (subjectCategory) {
            case 'obligatory':
                return null;
            case 'specialization':
                return (
                    <PillTag {...pillTagBase} variant="brightRed" type="solid" fontColor="light">
                        {specialization}
                    </PillTag>
                );
            case 'elective':
                return (
                    <PillTag {...pillTagBase} fontColor="turquoise" variant="turquoise" type="outline">
                        {elective}
                    </PillTag>
                );
            default:
                assertNever(subjectCategory);
        }
    };

    const formattedPoints = `${points.amount.toLocaleString(currentLocale)} ${creditsUnit}`;

    const formattedPrice = price && moneyToString(price);

    const renderDate = (date: Date) => {
        const formattedDate = formatDateShort(date, currentLanguage);

        return <span className={styles.StartDate}>{`${subjectStarting} ${formattedDate}`}</span>;
    };

    const renderTags = () => (
        <div className={styles.Tags}>
            {getPillTag()}
            <PillTag type="outline" variant="dark" size="responsiveFromXSmallToSmall">
                {formattedPoints}
            </PillTag>
        </div>
    );

    const renderSubject = () => (
        <div className={styles.Name}>
            {linkUrl ? (
                <div className={styles.LinkWrapper}>
                    <Link href={linkUrl} variant={LinkVariant.LightBlack}>
                        {subjectName}
                    </Link>
                    {withTags && startDate && renderDate(startDate)}
                </div>
            ) : (
                subjectName
            )}
        </div>
    );

    const renderInfo = () => (
        <>
            {withTags ? (
                renderTags()
            ) : (
                <div className={styles.Points}>
                    <span>{formattedPoints}</span>
                </div>
            )}
            {formattedPrice && (
                <div className={styles.Price}>
                    <span>{formattedPrice}</span>
                </div>
            )}
        </>
    );

    return (
        <div
            className={classNames(styles.SingleSubject, styles[`Variant--${displayType}`], styles[`Variant--${size}`])}
        >
            <div className={styles.Inner}>
                {renderSubject()}
                {renderInfo()}
                <div className={styles.Action}>
                    {canBePurchased && <SingleSubjectAction onClick={onActionClick} {...restProps} />}
                    {!canBePurchased && nonPurchasableSubjectsLink && (
                        <Link
                            {...nonPurchasableSubjectsLink}
                            type={LinkType.Button}
                            buttonType={ButtonType.Solid}
                            buttonTier={ButtonTier.Tertiary}
                            buttonSize={ButtonSize.Small}
                            showIsExternal={false}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
